function VehicleBookingService() {
    this.constructor.apply(this, arguments);
}

VehicleBookingService.$inject = ['$q', '$http', '$Config', 'AttackmentService','$mdPanel', '$translationService'];
VehicleBookingService.prototype = {
    /**
     * Constructor for the VehicleBookingService
     * @param $q {angular.IQService} - AngularJS promise service for handling asynchronous operations
     * @param $http {angular.IHttpService} - AngularJS HTTP service for making API requests
     * @param $Config {Object} - Configuration object containing application settings, including the API base URL
     * @param AttackmentService {AttackmentService} - Service for handling attackment-related operations
     * @param $mdPanel {$mdPanel} - Angular Material panel service for creating and managing panels
     * @param $translationService {$translationService} - Service for handling translations
     * @constructor
     */
    constructor($q, $http, $Config, AttackmentService, $mdPanel, $translationService) {
        this.$q = $q; // Assign AngularJS promise service
        this.$http = $http; // Assign AngularJS HTTP service
        this.$Config = $Config; // Assign configuration object
        this.attackmentService = AttackmentService;
        this.$mdPanel = $mdPanel;
        this.$translationService = $translationService;
    },

    /**
     * Helper function to construct the full API URL by appending the provided endpoint to the base URL
     * @param url {string} - The endpoint to append to the base API URL
     * @returns {string} - The full API URL
     */
    getUrl(url) {
        return this.$Config.apiUrl + url;
    },

    /**
     * Fetch all bookings
     * @returns {angular.IPromise<IVehicleBookings>} - A promise resolving to the user's bookings data
     */
    getMyBookings() {
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.get(this.getUrl('mobile/VehicleBooking/LoadVehicleBookings')).then(function (response) {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Fetch available vehicles within a specific date range
     * @param startDate {string} - The start date for the availability check
     * @param endDate {string} - The end date for the availability check
     * @returns {angular.IPromise<Array>} - A promise resolving to the list of available vehicles
     */
    getAvailableVehicles(startDate, endDate) {
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.post(this.getUrl('mobile/VehicleBooking/GetAvailableVehicles'), {
                From: startDate, // Start date for vehicle availability
                To: endDate // End date for vehicle availability
            }).then(function (response) {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Fetch all bookings within a specific date range
     * @param startDate {string} - The start date for the bookings query
     * @param endDate {string} - The end date for the bookings query
     * @returns {angular.IPromise<Array>} - A promise resolving to the list of all bookings
     */
    getAllBookings(startDate, endDate) {
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.post(this.getUrl('mobile/VehicleBooking/GetAllBookings'), {
                From: startDate, // Start date for bookings
                To: endDate // End date for bookings
            }).then(function (response) {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Fetch details of a specific vehicle booking by its ID
     * @param id {string} - The ID of the vehicle booking
     * @returns {angular.IPromise<IVehicleBooking>} - A promise resolving to the booking details
     */
    getVehicleBooking(id) {
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.get(this.getUrl('mobile/VehicleBooking/Get'), {
                params: {id: id} // Pass the booking ID as a query parameter
            }).then( (response) => {
                let booking = response.data;
                if(booking) {
                    if (booking.Attachments) {
                        this.attackmentService.buildImageUrl(booking.Attachments);
                    }
                    if (booking.PickUpQuestionnaire && booking.PickUpQuestionnaire.NewDamages) {
                        this.attackmentService.buildImageUrl(booking.PickUpQuestionnaire.NewDamages);
                    }

                    if (booking.ReturnQuestionnaire && booking.ReturnQuestionnaire.NewDamages) {
                        this.attackmentService.buildImageUrl(booking.ReturnQuestionnaire.NewDamages);
                    }


                    // for old version
                    if (booking.PickUpAttachments) {
                        this.attackmentService.buildImageUrl(booking.PickUpAttachments);
                    }
                    if (booking.ReturnAttachments) {
                        this.attackmentService.buildImageUrl(booking.ReturnAttachments);
                    }
                }

                resolve(booking); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Save a new or updated vehicle booking
     * @param data {Object} - The booking data to save
     * @returns {angular.IPromise<Object>} - A promise resolving to the saved booking details
     */
    saveVehicleBooking(data) {
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.post(this.getUrl('mobile/VehicleBooking/Save'), data).then(function (response) {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Mark a booking as picked up
     * @param data {IPickUpReturnSave} - The booking data to mark as picked up
     * @returns {angular.IPromise<Object>} - A promise resolving to the updated booking details
     */
    pickUpBooking(data) {
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.post(this.getUrl('mobile/VehicleBooking/PickUp'), data).then(function (response) {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Mark a booking as returned
     * @param data {IPickUpReturnSave} - The booking data to mark as returned
     * @returns {angular.IPromise<Object>} - A promise resolving to the updated booking details
     */
    returnBooking(data) {
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.post(this.getUrl('mobile/VehicleBooking/Return'), data).then(function (response) {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Remove a booking by its ID
     * @param bookingId {string}
     * @returns {angular.IPromise<unknown>}
     */
    removeBooking (bookingId) {
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.post(this.getUrl('mobile/VehicleBooking/Delete'), {
                Id: bookingId
            }).then(function (response) {
                resolve(response.data);
            }, reject);
        }));
    },
    /**
     * 
     * @param availableFrom {string}
     * @param availableTo {string}
     * @param searchText {string}
     * @param startIndex {number}
     * @param count {number}
     * @returns {angular.IPromise<{Rows: Vehicle[], TotalCount: number}>} - A promise resolving to the updated booking details
     */
    getVehiclesFromRange(availableFrom, availableTo, searchText,  startIndex, count) {
        // console.log('getVehiclesFromRange',arguments)
        // return this.$q(angular.bind(this, function (resolve, reject) {
        //     let list =[];
        //     for(let i = 0; i < 1000; i++) {
        //         list.push({
        //             Id:i,
        //             Name:'Name '+i,
        //             VehicleModel: 'VehicleModel '+i,
        //             PropulsionSystem: 'PropulsionSystem '+i,
        //             Transmission: 'Transmission '+i,
        //             Usage: i,
        //         })
        //     }
        //     list = list.filter((item)=>{
        //         return item.Name.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
        //     });
        //     let rows = list.slice(startIndex, startIndex + count);
        //     data={
        //         Rows: rows,
        //         TotalCount: list.length
        //     }
        //     setTimeout(()=>{
        //         resolve(data);
        //     },2000)
        // }));

        
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.get(this.getUrl('mobile/VehicleBooking/Input/Vehicle'), {
                params:{
                    availableFrom: availableFrom,
                    availableTo: availableTo,
                    searchText: searchText, 
                    startIndex: startIndex,
                    count: count
                }

            }).then(function (response) {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

     /**
     * Change the vehicle of a booking
     * @returns {angular.IPromise<Vehicle[]>} - A promise resolving to the updated booking details
     */
    changeVehicle(data) {
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.post(this.getUrl('mobile/VehicleBooking/ChangeVehicle'), data).then(function (response) {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Get the vehicle procedure
     * @param type {string}
     * @returns {angular.IPromise<IVehicleProcedure>}
     */
    getVehicleProcedure(type){
        return this.$q(angular.bind(this, function (resolve, reject) {
             this.$http.get(this.getUrl('mobile/VehicleBooking/GetVehicleProcedure'), {
                params:{
                    type: type
                }
            }).then(function (response) {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Get the vehicle procedure
     * @param vehicleId {string}
     * @returns {angular.IPromise<IVehicleDamage[]>}
     */
    getExistingVehicleDamages(vehicleId){
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.get(this.getUrl('mobile/VehicleBooking/GetExistingVehicleDamages'), {
                params:{
                    vehicleId: vehicleId
                }
            }).then( (response) => {
                let vehicleDamages = response.data;

                // Build Url for damages
                if(vehicleDamages){
                    this.attackmentService.buildImageUrl(vehicleDamages);
                }

                resolve(vehicleDamages); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },


    /**
     *
     * @param damage {IVehicleDamage}
     * @returns {IAttachment}
     */
    getAttachmentFromDamage(damage) {
        let name = damage.DocumentName;
        if (!damage.DocumentId) {
            name = this.getLocationName(damage.Location);
        }
        return {
            Id: damage.Id,
            Name: name,
            DocumentId: damage.DocumentId,
            ThumbnailUrl: damage.ThumbnailUrl,
            DownloadUrl: damage.DownloadUrl,
            iconClass: damage.DocumentName ? this.attackmentService.getIcon(damage.DocumentName) : '',
            isPhoto: !!damage.DocumentId,
            isNew: damage.isNew
        };

    },

    /**
     * Show damage details
     * @param damage {IVehicleDamage}
     * @returns {*}
     */
    onViewDamageDetail(damage) {
        let panelRef = this.$mdPanel.create({
            controller: 'ViewDamageDetailsController',
            controllerAs: 'vm',
            templateUrl: 'js/internal/vehiclebookings/dialogs/view-damage-details/view-damage-details.html',
            parent: angular.element(document.body),
            hasBackdrop: true,
            fullscreen: true,
            escapeToClose: true,
            panelClass: 'panel-md-dialog',
            focusOnOpen: false,
            locals: {
                close: (result) => {
                    return panelRef.close(result);
                },
                damage: damage
            }
        });
        return panelRef.open().then((panel) => {
            panel._config.scope.vm.ready();
        });
    },

    /**
     * get Locations
     * @returns {IVehicleDamageLocationItem[]}
     */
    getLocations() {
        /** @type {IVehicleDamageLocationItem[]} */
        return [
            {isGroup: true, Name: 'Front'},
            {Id: 2, Name: 'FrontBumper'},
            {Id: 3, Name: 'FrontHood'},
            {Id: 4, Name: 'FrontWindshield'},
            {Id: 5, Name: 'LeftHeadlight'},
            {Id: 6, Name: 'RightHeadlight'},
            {isGroup: true, Name: 'Left'},
            {Id: 8, Name: 'DriverFrontDoor'},
            {Id: 9, Name: 'DriverBackDoor'},
            {Id: 10, Name: 'DriverSideMirror'},
            {Id: 11, Name: 'LeftSideOther'},
            {isGroup: true, Name: 'Right'},
            {Id: 13, Name: 'PassengerFrontDoor'},
            {Id: 14, Name: 'PassengerBackDoor'},
            {Id: 15, Name: 'PassengerSideMirror'},
            {Id: 16, Name: 'RightSideOther'},
            {isGroup: true, Name: 'Rear'},
            {Id: 18, Name: 'RearBumper'},
            {Id: 19, Name: 'Trunk'},
            {Id: 20, Name: 'RearWindshield'},
            {Id: 21, Name: 'LeftTaillight'},
            {Id: 22, Name: 'RightTaillight'},
            {isGroup: true, Name: 'WheelsAndMisc'},
            {Id: 24, Name: 'FrontLeftWheel'},
            {Id: 25, Name: 'FrontRightWheel'},
            {Id: 26, Name: 'RearLeftWheel'},
            {Id: 27, Name: 'RearRightWheel'},
            {Id: 28, Name: 'Other'}
        ].map((location) => {
            location.FullName = this.getLocationNameFromLocation(location);
            return location;
        });
    },



    /**
     * Get Location name from location
     * @param location {IVehicleDamageLocationItem}
     * @return {string}
     */
    getLocationNameFromLocation(location) {
        if(!location){
            return "";
        }
        let name = 'VehicleDamageLocation';
        if (location.isGroup) {
            name = name + "Group";
        }
        name = name + "_" + location.Name;
        return this.$translationService.getTranslation(name)
    },

    /**
     *
     * @param name {string}
     */
    getLocationName(name){
        let locations = this.getLocations();
        let location = locations.find(location => location.Name === name);
        return this.getLocationNameFromLocation(location);
    },

    /**
     *
     * @param bookingId {string}
     * @returns {angular.IPromise<IVehicle>}
     */
    getVehicleForReturnedBooking(bookingId){
        return this.$q(angular.bind(this, function (resolve, reject) {
            this.$http.get(this.getUrl('mobile/VehicleBooking/GetVehicleForReturnedBooking'), {
                params:{
                    Id: bookingId
                }
            }).then( (response) => {
                resolve(response.data); // Resolve the promise with the response data
            }, reject); // Reject the promise if the HTTP request fails
        }));
    },

    /**
     * Check vehicle is electric
     * @param vehiclePropulsion {string}
     */
    isElectricVehicle(vehiclePropulsion) {
        return vehiclePropulsion.toLowerCase().indexOf('Electric'.toLowerCase()) >= 0;
    }
}


angular.module('internal.vehiclebookings').service('VehicleBookingService', VehicleBookingService);